import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ImageCarousel.module.scss';
import { ReactComponent as CloseIcon } from '../../pages/NarrativePage/assets/close.svg';
import { ReactComponent as LeftArrowIcon } from '../../assets/chevronLeft.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/chevronRight.svg';
import { ReactComponent as BrokenImageIcon } from '../../assets/brokenImage.svg';

export const ImageCarousel = ({ images, initialIndex = 0, isOpen, onClose }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [avifSupported, setAvifSupported] = useState(true); 

  const checkAVIFSupport = async () => {
    try {
      const img = new Image();
      img.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEAwgMg8f8D///8WfhwB8+ErK42A=';
      await img.decode();
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    checkAVIFSupport().then((supported) => {
      setAvifSupported(supported);
    });
  }, []);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const handleClose = () => {
    onClose(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowRight') {
      handleNext();
    } else if (event.key === 'ArrowLeft') {
      handlePrev();
    } else if (event.key === 'Escape') {
      handleClose();
    }
  };

  const handleClickInside = (event) => {
    if (
      event.button !== 2 && // Check if the right mouse button is not clicked
      !event.target.closest(`.${styles.prevButton}`) &&
      !event.target.closest(`.${styles.nextButton}`) &&
      !event.target.closest(`.${styles.closeButton}`)
    ) {
      handleClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleClickInside);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickInside);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickInside);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.carousel}>
      <button className={styles.closeButton} onClick={handleClose}>
        <CloseIcon />
      </button>
      <button className={styles.prevButton} onClick={handlePrev}>
        <LeftArrowIcon />
      </button>
      {avifSupported ? (
        <img
          src={images[currentIndex]}
          alt={`Slide ${currentIndex}`}
          className={styles.image}
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null; // Prevents infinite loop in case of broken image
            setAvifSupported(false); // Set to false if image fails to load
          }}
        />
      ) : (
        <div className={styles.unsupportedMessage}>
          <BrokenImageIcon />
          <h4>{t('Image error')}</h4>
          <p>{t('Please update your browser')}</p>
        </div>
      )}
      <button className={styles.nextButton} onClick={handleNext}>
        <RightArrowIcon />
      </button>
    </div>
  );
};

